import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  DatePicker,
  Input,
  Modal,
  Pagination,
  Select,
  Table,
  Tabs,
  message,
} from 'antd';
import { Button as CrceButton, Spinner } from '@crc-evans/ui-components';
import {
  Activity,
  AlertTriangle,
  Download,
  History,
  RefreshCw,
  Search,
  Shield,
} from 'lucide-react';
import moment from 'moment';
import get from 'lodash/get';
import axiosConfig from '../../services/axiosConfig';
import {
  exportAuditTrailCsv,
  fetchAuditInsights,
  fetchAuditTrail,
  fetchAuditTrailDashboardStats,
  fetchEntityAuditHistory,
  fetchUserActivityTimeline,
} from '../../services/auditTrailApi';
import {
  logActivityExport,
  logActivityFilterApplied,
  logActivityFilterCleared,
  logActivityPageView,
  logActivitySearch,
} from '../../features/audit/activityAudit';
import '../../pages/technical/HeatProfilePage.css';
import './AuditTrailView.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;

const MODULES = [
  'ACTIVITY',
  'COGNITO',
  'PROJECT',
  'EQUIPMENT',
  'MAINTENANCE',
  'DSR',
  'DPR',
  'PQT',
  'SPARES',
  'MATERIALS',
  'EMPLOYEE',
  'SHIPPING',
  'REPORTS',
  'SECURITY',
  'SYSTEM',
];

const popupToBody = () => document.body;

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

/** e.g. 24 Sept 2026, 17:16:16 — matches Data360 audit trail */
function formatWhen(iso) {
  if (!iso) return '—';
  const m = moment(iso);
  if (!m.isValid()) return String(iso);
  return `${m.date()} ${MONTH_SHORT[m.month()]} ${m.year()}, ${m.format('HH:mm:ss')}`;
}

function severityClass(sev) {
  const s = String(sev || 'INFO').toUpperCase();
  if (s === 'ERR' || s === 'ERROR') return 'audit-sev--err';
  if (s === 'WARN' || s === 'WARNING') return 'audit-sev--warn';
  return 'audit-sev--info';
}

/**
 * Shared Event log / User activity / Entity history dashboard.
 * Admin: enableProjectFilter. Project: pass projectId (+ optional projectName).
 */
export default function AuditTrailView({
  projectId: fixedProjectId,
  projectName: fixedProjectName,
  enableProjectFilter = false,
  description = 'Activity log for compliance and troubleshooting.',
}) {
  const [viewMode, setViewMode] = useState('log');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [module, setModule] = useState(undefined);
  const [severity, setSeverity] = useState(undefined);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [projectFilter, setProjectFilter] = useState(undefined);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  /** all | be | ui — default API changes */
  const [eventSource, setEventSource] = useState('be');
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);

  const [actorId, setActorId] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [entityHistory, setEntityHistory] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);

  const resolvedProjectId = enableProjectFilter
    ? projectFilter != null && projectFilter !== ''
      ? Number(projectFilter)
      : undefined
    : fixedProjectId != null
      ? Number(fixedProjectId)
      : undefined;

  const resolvedProjectName = enableProjectFilter
    ? projectOptions.find((p) => Number(p.id) === Number(projectFilter))?.projectName ||
      projectOptions.find((p) => Number(p.id) === Number(projectFilter))?.name
    : fixedProjectName;

  useEffect(() => {
    if (!enableProjectFilter) return undefined;
    let cancelled = false;
    (async () => {
      setProjectsLoading(true);
      try {
        const res = await axiosConfig.get('/project');
        const list = get(res, ['data', 'data'], get(res, ['data'], []));
        const rows = Array.isArray(list) ? list : [];
        if (!cancelled) {
          setProjectOptions(
            rows
              .map((p) => ({
                id: p.id,
                projectName: p.projectName || p.name || '',
                projectId: p.projectId || '',
              }))
              .filter((p) => p.id != null)
              .sort((a, b) =>
                String(a.projectName).localeCompare(String(b.projectName), undefined, {
                  sensitivity: 'base',
                })
              )
          );
        }
      } catch {
        if (!cancelled) setProjectOptions([]);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enableProjectFilter]);

  useEffect(() => {
    logActivityPageView(enableProjectFilter ? 'Admin Audit Trail' : 'Project Audit Trail', {
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
    });
  }, [enableProjectFilter, resolvedProjectId, resolvedProjectName]);

  const queryParams = useMemo(() => {
    const params = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      sortBy: 'created_at',
      sortOrder: 'DESC',
    };
    if (resolvedProjectId != null && Number.isFinite(resolvedProjectId)) {
      params.project_id = resolvedProjectId;
    }
    if (module) params.module = module;
    if (severity) params.severity = severity;
    if (eventSource && eventSource !== 'all') params.source = eventSource;
    if (search.trim()) params.search = search.trim();
    if (dateRange?.[0]) params.created_from = dateRange[0].clone().startOf('day').toISOString();
    if (dateRange?.[1]) params.created_to = dateRange[1].clone().endOf('day').toISOString();
    return params;
  }, [page, pageSize, resolvedProjectId, module, severity, search, dateRange, eventSource]);

  const loadLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [listRes, statsRes, insightsRes] = await Promise.all([
      fetchAuditTrail(queryParams),
      fetchAuditTrailDashboardStats(queryParams),
      fetchAuditInsights('summary', queryParams),
    ]);
    setLoading(false);
    if (!listRes.ok) {
      setError(listRes.error || 'Failed to load');
      setItems([]);
      setTotal(0);
      return;
    }
    setItems(listRes.data.items || []);
    setTotal(listRes.data.total || 0);
    if (statsRes.ok) setStats(statsRes.data);
    else if (insightsRes.ok) {
      setStats({
        total_events: insightsRes.data?.kpis?.total_events,
        events_today: insightsRes.data?.kpis?.events_today,
        failure_events: insightsRes.data?.kpis?.failure_events,
        security_events: insightsRes.data?.kpis?.security_events,
      });
    }
  }, [queryParams]);

  useEffect(() => {
    if (viewMode === 'log') loadLog();
  }, [viewMode, loadLog]);

  const applyFilters = () => {
    setPage(1);
    logActivityFilterApplied(
      'audit-trail',
      { module, severity, search, projectId: resolvedProjectId, projectName: resolvedProjectName, source: eventSource },
      resolvedProjectId,
      resolvedProjectName
    );
    if (search.trim()) {
      logActivitySearch('audit-trail', search, resolvedProjectId, resolvedProjectName);
    }
  };

  const clearFilters = () => {
    setModule(undefined);
    setSeverity(undefined);
    setSearch('');
    setDateRange(null);
    setProjectFilter(undefined);
    setEventSource('be');
    setPage(1);
    logActivityFilterCleared('audit-trail', resolvedProjectId, resolvedProjectName);
  };

  const handleExport = async () => {
    try {
      const blob = await exportAuditTrailCsv(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pih-audit-trail-${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      logActivityExport('audit-trail-csv', {
        projectId: resolvedProjectId,
        projectName: resolvedProjectName,
      });
      message.success('Export started');
    } catch (e) {
      message.error(e?.message || 'Export failed');
    }
  };

  const loadTimeline = async () => {
    if (!actorId.trim()) {
      message.warning('Enter an actor email or id');
      return;
    }
    setTimelineLoading(true);
    const res = await fetchUserActivityTimeline({
      actor_id: actorId.trim(),
      project_id: resolvedProjectId,
      limit: 100,
    });
    setTimelineLoading(false);
    if (!res.ok) {
      message.error(res.error);
      setTimeline(null);
      return;
    }
    setTimeline(res.data);
  };

  const normalizeEntityHistoryList = (data) => {
    if (!data) return [];
    const list = data.history || data.items || data.audit_trail || data.events || [];
    return Array.isArray(list) ? list : [];
  };

  const loadEntity = async (typeOverride, idOverride) => {
    const type = String(typeOverride ?? entityType).trim();
    const id = String(idOverride ?? entityId).trim();
    if (!type || !id) {
      message.warning('Entity type and id are required');
      return;
    }
    setEntityType(type);
    setEntityId(id);
    setEntityLoading(true);
    const res = await fetchEntityAuditHistory({
      entity_type: type,
      entity_id: id,
      project_id: resolvedProjectId,
      limit: 100,
    });
    setEntityLoading(false);
    if (!res.ok) {
      message.error(res.error);
      setEntityHistory({ history: [] });
      return;
    }
    const list = normalizeEntityHistoryList(res.data);
    setEntityHistory({ ...(res.data || {}), history: list });
  };

  const openEntityHistoryFromDetail = () => {
    if (!detail?.entity_type || detail?.entity_id == null || detail?.entity_id === '') {
      message.warning('This event has no entity type + id');
      return;
    }
    const type = String(detail.entity_type);
    const id = String(detail.entity_id);
    setDetail(null);
    setViewMode('entity');
    loadEntity(type, id);
  };

  const entityHistoryItems = normalizeEntityHistoryList(entityHistory);

  const renderSeverity = (v) => (
    <span className={`audit-sev ${severityClass(v)}`}>{v || 'INFO'}</span>
  );

  const stringifyVal = (v) => {
    if (v == null) return '—';
    if (typeof v === 'string') return v;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  const formatChangePreview = (row) => {
    const fields = row?.changed_fields;
    if (Array.isArray(fields) && fields.length) {
      return fields
        .slice(0, 2)
        .map((f) => `${f.field}: ${stringifyVal(f.from)} → ${stringifyVal(f.to)}`)
        .join('; ');
    }
    if (row?.previous || row?.changes) {
      return 'View details';
    }
    return '—';
  };

  const resolveSource = (row) => {
    if (row?.source) return String(row.source).toUpperCase();
    if (String(row?.module || '').toUpperCase() === 'ACTIVITY') return 'UI';
    return 'BE';
  };

  const formatModuleLabel = (row) => {
    const mod = row?.module || '—';
    const src = resolveSource(row);
    if (String(mod).toUpperCase() === 'ACTIVITY' || src === 'UI') {
      return mod === '—' ? 'User activity (UI)' : `${mod} (UI)`;
    }
    return mod;
  };

  const columns = [
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 200,
      render: (v) => formatWhen(v),
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      width: 180,
      ellipsis: true,
      render: (v, row) => v || row.project_id || '—',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 100,
      render: (v) => renderSeverity(v),
    },
    {
      title: 'Module',
      key: 'module',
      width: 170,
      ellipsis: true,
      render: (_, row) => formatModuleLabel(row),
    },
    {
      title: 'Event',
      dataIndex: 'event_label',
      width: 200,
      ellipsis: true,
      render: (v, row) => v || row.event_type,
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      width: 260,
      ellipsis: true,
      render: (v, row) => v || row.display_title || '—',
    },
    {
      title: 'Change',
      key: 'change',
      width: 240,
      ellipsis: true,
      render: (_, row) => formatChangePreview(row),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 180,
      ellipsis: true,
      render: (_, row) => {
        if (!row.entity_type && row.entity_id == null) return '—';
        return (
          <span className="audit-entity-cell">
            <span className="audit-entity-cell__id">
              {row.entity_name || row.entity_id || '—'}
            </span>
            {row.entity_type ? (
              <span className="audit-entity-cell__type">{row.entity_type}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      title: 'Actor',
      dataIndex: 'actor_email',
      width: 220,
      ellipsis: true,
      render: (v, row) => v || row.actor_name || row.actor_id || '—',
    },
    {
      title: 'Details',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, row) => (
        <button type="button" className="audit-view-link" onClick={() => setDetail(row)}>
          View
        </button>
      ),
    },
  ];

  const historyColumns = [
    { title: 'When', dataIndex: 'created_at', width: 200, render: (v) => formatWhen(v) },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 100,
      render: (v) => renderSeverity(v),
    },
    {
      title: 'Event',
      dataIndex: 'event_label',
      width: 200,
      ellipsis: true,
      render: (v, row) => v || row.event_type,
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      width: 260,
      ellipsis: true,
      render: (v, row) => v || row.display_title || '—',
    },
    {
      title: 'Change',
      key: 'change',
      width: 240,
      ellipsis: true,
      render: (_, row) => formatChangePreview(row),
    },
    {
      title: 'Actor',
      dataIndex: 'actor_email',
      width: 220,
      ellipsis: true,
      render: (v, row) => v || row.actor_name || row.actor_id || '—',
    },
    {
      title: 'Details',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <button type="button" className="audit-view-link" onClick={() => setDetail(row)}>
          View
        </button>
      ),
    },
  ];

  const tableLoading = {
    spinning: loading,
    indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
  };

  return (
    <div className="modern-audit-trail-page">
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card audit-trail-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div
                className="heat-profile-icon-wrap"
                style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
              >
                <History size={22} color="currentColor" strokeWidth={2} />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">Audit Trail</h3>
                </div>
                <p className="heat-profile-list-description">{description}</p>
              </div>
            </div>
            <div className="heat-profile-header-actions calibration-modern-actions audit-trail-header-actions">
              <CrceButton
                label="Refresh"
                variant="secondary"
                size="small"
                icon={RefreshCw}
                onClick={() => (viewMode === 'log' ? loadLog() : null)}
              />
              <CrceButton
                label="Export CSV"
                variant="tertiary"
                size="small"
                icon={Download}
                onClick={handleExport}
              />
            </div>
          </div>

          {stats && viewMode === 'log' && (
            <div className="audit-stats-row">
              <div className="audit-stat-card" style={{ '--stat-color': '#2563eb' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}>
                  <Activity size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.total_events ?? 0}</span>
                  <span className="audit-stat-label">Total events</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#00a88e' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#ecfdf5', color: '#00a88e' }}>
                  <History size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.events_today ?? 0}</span>
                  <span className="audit-stat-label">Today</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#dc2626' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                  <AlertTriangle size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.failure_events ?? 0}</span>
                  <span className="audit-stat-label">Failures</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#7c3aed' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#f5f3ff', color: '#7c3aed' }}>
                  <Shield size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.security_events ?? 0}</span>
                  <span className="audit-stat-label">Security</span>
                </div>
              </div>
            </div>
          )}

          <div className="audit-trail-body">
            <Tabs
              className="audit-trail-tabs"
              activeKey={viewMode}
              onChange={setViewMode}
            >
              <TabPane tab="Event log" key="log">
                <div className="audit-source-tabs" role="tablist" aria-label="Event source">
                  {[
                    { key: 'be', label: 'API changes' },
                    { key: 'ui', label: 'UI actions' },
                    { key: 'all', label: 'All events' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={eventSource === opt.key}
                      className={`audit-source-tabs__btn${
                        eventSource === opt.key ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setEventSource(opt.key);
                        setPage(1);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="audit-filters">
                  <div
                    className={`audit-filters-row audit-filters-row--primary${
                      enableProjectFilter ? ' audit-filters-row--with-project' : ''
                    }`}
                  >
                    {enableProjectFilter && (
                      <Select
                        showSearch
                        allowClear
                        loading={projectsLoading}
                        placeholder="Search project by name…"
                        className="audit-filter-select audit-filter-project"
                        value={projectFilter}
                        onChange={(val) => {
                          setProjectFilter(val);
                          setPage(1);
                        }}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '')
                            .toLowerCase()
                            .includes(String(input).toLowerCase())
                        }
                        options={projectOptions.map((p) => ({
                          value: p.id,
                          label: p.projectName
                            ? p.projectId
                              ? `${p.projectName} (${p.projectId})`
                              : p.projectName
                            : `Project ${p.id}`,
                        }))}
                        dropdownClassName="modern-dropdown-popup"
                        getPopupContainer={popupToBody}
                      />
                    )}
                    <Select
                      allowClear
                      placeholder="Module"
                      className="audit-filter-select audit-filter-module"
                      value={module}
                      onChange={setModule}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                    >
                      {MODULES.map((m) => (
                        <Option key={m} value={m}>
                          {m}
                        </Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      placeholder="Severity"
                      className="audit-filter-select audit-filter-severity"
                      value={severity}
                      onChange={setSeverity}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                    >
                      <Option value="INFO">INFO</Option>
                      <Option value="WARN">WARN</Option>
                      <Option value="ERR">ERR</Option>
                    </Select>
                    <Input
                      className="audit-search-input"
                      allowClear
                      placeholder="Search actor, entity, event, summary…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onPressEnter={applyFilters}
                      prefix={<Search size={15} className="audit-search-icon" />}
                    />
                  </div>
                  <div className="audit-filters-row audit-filters-row--secondary">
                    <RangePicker
                      className="audit-range-picker"
                      value={dateRange}
                      onChange={setDateRange}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                      placement="bottomLeft"
                    />
                    <div className="audit-filters-actions">
                      <CrceButton
                        label="Apply"
                        variant="primary"
                        size="small"
                        onClick={applyFilters}
                      />
                      <CrceButton
                        label="Clear"
                        variant="secondary"
                        size="small"
                        onClick={clearFilters}
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
                )}

                <div className="spares-list-table-pager-wrap pipeline-table audit-table-pager-wrap">
                  <div className="audit-table-wrap heat-profile-table-wrap">
                    <Table
                      className="audit-table heat-profile-table"
                      rowKey="id"
                      size="middle"
                      loading={tableLoading}
                      columns={columns}
                      dataSource={items}
                      pagination={false}
                      scroll={{ x: 1840 }}
                    />
                  </div>
                  {total > 0 && (
                    <div className="spares-list-pager-outer">
                      <Pagination
                        className="heat-profile-pagination"
                        current={page}
                        pageSize={pageSize}
                        total={total}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} items`}
                        onChange={(p, size) => {
                          setPage(p);
                          if (size != null) setPageSize(size);
                        }}
                      />
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tab="User activity" key="timeline">
                <div className="audit-filters">
                  <div className="audit-filters-row">
                    <Input
                      className="audit-filter-input"
                      style={{ width: 280 }}
                      placeholder="Actor email or id"
                      value={actorId}
                      onChange={(e) => setActorId(e.target.value)}
                      onPressEnter={loadTimeline}
                    />
                    <CrceButton
                      label="Load timeline"
                      variant="primary"
                      size="small"
                      onClick={loadTimeline}
                      disabled={timelineLoading}
                    />
                  </div>
                </div>
                {timelineLoading && (
                  <div className="audit-inline-loading">
                    <Spinner variant="inline" size="medium" color="#00C9A7" />
                  </div>
                )}
                {timeline?.sessions?.length ? (
                  timeline.sessions.map((session) => (
                    <div key={session.session_id} className="audit-session-card">
                      <div className="audit-session-head">
                        <span className="audit-session-title">Session {session.session_id}</span>
                        <span className="audit-session-meta">{session.event_count} events</span>
                      </div>
                      <div className="audit-table-wrap">
                        <Table
                          className="audit-table"
                          rowKey="id"
                          size="middle"
                          pagination={false}
                          columns={historyColumns}
                          dataSource={session.events || []}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  !timelineLoading && (
                    <p className="audit-empty-hint">
                      Enter an actor and load their session-grouped activity.
                    </p>
                  )
                )}
              </TabPane>

              <TabPane tab="Entity history" key="entity">
                <div className="audit-filters">
                  <div className="audit-filters-row">
                    <Input
                      className="audit-filter-input"
                      style={{ width: 200 }}
                      placeholder="Entity type (e.g. USER)"
                      value={entityType}
                      onChange={(e) => setEntityType(e.target.value)}
                    />
                    <Input
                      className="audit-filter-input"
                      style={{ width: 220 }}
                      placeholder="Entity id"
                      value={entityId}
                      onChange={(e) => setEntityId(e.target.value)}
                      onPressEnter={() => loadEntity()}
                    />
                    <CrceButton
                      label="Load history"
                      variant="primary"
                      size="small"
                      onClick={() => loadEntity()}
                      disabled={entityLoading}
                    />
                  </div>
                </div>
                {entityLoading && (
                  <div className="audit-inline-loading">
                    <Spinner variant="inline" size="medium" color="#00C9A7" />
                  </div>
                )}
                {entityHistory != null && !entityLoading ? (
                  entityHistoryItems.length > 0 ? (
                    <div className="audit-table-wrap">
                      <Table
                        className="audit-table"
                        rowKey={(r) => String(r.id ?? `${r.created_at}-${r.event_type}`)}
                        size="middle"
                        loading={false}
                        columns={historyColumns}
                        dataSource={entityHistoryItems}
                        pagination={false}
                      />
                    </div>
                  ) : (
                    <p className="audit-empty-hint">
                      No history found for entity type <strong>{entityType}</strong> + id{' '}
                      <strong>{entityId}</strong>.
                    </p>
                  )
                ) : (
                  !entityLoading && (
                    <p className="audit-empty-hint">
                      Look up mutations for a specific entity type + id. Tip: open an Event log
                      row → View → use &quot;View entity history&quot;.
                    </p>
                  )
                )}
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>

      <Modal
        visible={!!detail}
        title={detail?.event_label || detail?.event_type || 'Event detail'}
        onCancel={() => setDetail(null)}
        footer={null}
        width={720}
        className="audit-detail-modal"
        wrapClassName="audit-detail-modal-wrap"
        destroyOnClose
      >
        {detail && (
          <div className="audit-detail-body">
            <p>
              <strong>When:</strong> {formatWhen(detail.created_at)}
            </p>
            <p>
              <strong>Source:</strong> {resolveSource(detail)}
            </p>
            <p>
              <strong>Project:</strong>{' '}
              {detail.project_name || detail.project_id || '—'}
            </p>
            <p>
              <strong>Severity:</strong> {detail.severity || 'INFO'}
            </p>
            <p>
              <strong>Module:</strong> {detail.module || '—'}
            </p>
            <p>
              <strong>Summary:</strong> {detail.summary || '—'}
            </p>
            <p>
              <strong>Actor:</strong>{' '}
              {detail.actor_name || detail.actor_email || detail.actor_id || '—'}
            </p>
            <p className="audit-detail-entity-row">
              <strong>Entity:</strong>{' '}
              <span>
                {detail.entity_type || '—'} / {detail.entity_id ?? '—'}
                {detail.entity_name ? ` (${detail.entity_name})` : ''}
              </span>
              {detail.entity_type && detail.entity_id != null && detail.entity_id !== '' && (
                <button
                  type="button"
                  className="audit-view-link audit-entity-history-link"
                  onClick={openEntityHistoryFromDetail}
                >
                  View entity history
                </button>
              )}
            </p>
            <p>
              <strong>Session:</strong> {detail.session_id || '—'}
            </p>

            {(detail.previous || detail.changes || (detail.changed_fields || []).length > 0) && (
              <div className="audit-detail-change-block">
                <strong>What changed</strong>
                {Array.isArray(detail.changed_fields) && detail.changed_fields.length > 0 ? (
                  <table className="audit-change-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Previous</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.changed_fields.map((f) => (
                        <tr key={f.field}>
                          <td>{f.field}</td>
                          <td>
                            <code>{stringifyVal(f.from)}</code>
                          </td>
                          <td>
                            <code>{stringifyVal(f.to)}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="audit-change-panels">
                    <div>
                      <div className="audit-change-panel-label">Previous</div>
                      <pre>{JSON.stringify(detail.previous ?? null, null, 2)}</pre>
                    </div>
                    <div>
                      <div className="audit-change-panel-label">New / changes</div>
                      <pre>{JSON.stringify(detail.changes ?? null, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="audit-detail-actions-block">
              <strong>Full action details</strong>
              {detail.action_details != null ? (
                <pre>{JSON.stringify(detail.action_details, null, 2)}</pre>
              ) : (
                <p className="audit-empty-hint">No action details recorded for this event.</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
